import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import type { ChatMessage } from "@/services/api/scenarios/types";
import { ANI_CHAT_SIDEBAR_WIDTH_PX } from "@/components/chat/chatMessageChrome";
import type { FeToolCall } from "@/services/api/in-app-agent";

// Re-export for convenience
export type { ChatMessage };

// Defines the possible chat contexts for AniChatWidget
export type AniChatWidgetContextValue =
  | "scenarios"
  | "documents"
  | "cash-flow"
  | "pod"
  | "dashboard"
  | "screener"
  | "playground"
  | "product"
  | "portfolio"
  | "worker"
  | "report-builder"
  | "case-checking"
  | undefined;

// Defines the configuration properties for the AniChatWidget that can be managed by this context
export interface AniConfig {
  resourceId?: string;
  chatContext?: AniChatWidgetContextValue;
  apiData?: unknown;
  onApiAction?: (action: {
    endpoint: string;
    method: string;
    data: unknown;
    explanation: string;
  }) => void;
  samplePrompts?: string[];
  appSamplePrompts?: Record<string, string[]>;
  initialAssistantMessage?: string;
  /** When true, the AniChatWidget is hidden */
  hidden?: boolean;
}

export type AniWidgetMode = "docked" | "floating";

export type AniDisplayMode = "normal" | "developer";

/**
 * Queued user message to send through Ani chat when the sidebar/widget opens.
 * The `id` must change per request so repeat sends with the same text still run.
 */
export interface OutboundUserMessagePayload {
  id: number;
  content: string;
}

/**
 * A message posted into Ani's chat by the app rather than typed or generated in a turn
 * (e.g. Ani reacting to a mission event). `toolCall` runs after it shows, such as opening a window.
 */
export interface AniAnnouncement {
  id: string;
  role: "assistant" | "user";
  content: string;
  toolCall?: FeToolCall | null;
}

/** Accepts files dropped onto Ani. Only one feature can own drops at a time. */
export interface AniFileDropHandler {
  label: string;
  /** Lower-case file extensions, e.g. ".eml". */
  accepts: readonly string[];
  onFile: (file: File) => Promise<void>;
}

/**
 * Ani personality modes that control tone and communication style.
 * - professional: Formal, concise, suitable for client-facing scenarios
 * - casual: Relaxed and natural everyday tone
 * - fun: Lively and playful with personality
 */
export type AniPersonalityMode = "professional" | "casual" | "fun";

export const ANI_PERSONALITY_MODES: {
  id: AniPersonalityMode;
  label: string;
  description: string;
}[] = [
    {
      id: "professional",
      label: "Professional",
      description: "Client-ready",
    },
    {
      id: "casual",
      label: "Casual",
      description: "Relaxed and natural",
    },
    {
      id: "fun",
      label: "Fun",
      description: "Lively and playful",
    },
  ];

export interface AniWidgetViewState {
  mode: AniWidgetMode;
  isOpen: boolean;
  isSpaceReady: boolean;
  displayMode: AniDisplayMode;
  personalityMode: AniPersonalityMode;
  floatingPosition: {
    x: number;
    y: number;
  };
  floatingSize: {
    width: number;
    height: number;
  };
  dockedWidth: number;
}

const PERSONALITY_MODE_STORAGE_KEY = "ani_personality_mode";
const SIDEBAR_OPEN_STORAGE_KEY = "ani_sidebar_open";
const SIDEBAR_WIDTH_STORAGE_KEY = "ani_sidebar_width";

const getStoredPersonalityMode = (): AniPersonalityMode => {
  if (typeof window === "undefined") return "casual";
  const stored = localStorage.getItem(PERSONALITY_MODE_STORAGE_KEY);
  if (stored === "professional" || stored === "casual" || stored === "fun") {
    return stored;
  }
  return "casual";
};

const getStoredSidebarOpen = (): boolean => {
  if (typeof window === "undefined") return false;
  const stored = localStorage.getItem(SIDEBAR_OPEN_STORAGE_KEY);
  return stored === "true";
};

const getStoredSidebarWidth = (): number => {
  if (typeof window === "undefined") return ANI_CHAT_SIDEBAR_WIDTH_PX;
  const stored = localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY);
  if (!stored) return ANI_CHAT_SIDEBAR_WIDTH_PX;
  const parsed = Number.parseFloat(stored);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : ANI_CHAT_SIDEBAR_WIDTH_PX;
};

const DEFAULT_ANI_WIDGET_VIEW_STATE: AniWidgetViewState = {
  mode: "docked",
  isOpen: false,
  isSpaceReady: false,
  displayMode: "normal",
  personalityMode: "casual",
  floatingPosition: {
    x: 0,
    y: 0,
  },
  floatingSize: {
    width: 400,
    height: 600,
  },
  dockedWidth: ANI_CHAT_SIDEBAR_WIDTH_PX,
};

// Defines the shape of the context value, including the config and updater functions
interface AniContextProps extends AniConfig {
  setAniConfig: (config: Partial<AniConfig>) => void;
  setResourceId: (id: string | undefined) => void;
  setChatContext: (context: AniChatWidgetContextValue) => void;
  setApiData: (data: unknown) => void;
  setOnApiAction: (callback: AniConfig["onApiAction"]) => void;
  setSamplePrompts: (prompts: string[] | undefined) => void;
  setAppSamplePrompts: (prompts: Record<string, string[]> | undefined) => void;
  setInitialAssistantMessage: (message: string | undefined) => void;
  widgetViewState: AniWidgetViewState;
  setWidgetViewState: (state: Partial<AniWidgetViewState>) => void;
  toggleWidgetOpen: () => void;
  setDisplayMode: (mode: AniDisplayMode) => void;
  toggleDisplayMode: () => void;
  setPersonalityMode: (mode: AniPersonalityMode) => void;
  // Conversation history management
  conversationHistory: ChatMessage[];
  setConversationHistory: (history: ChatMessage[]) => void;
  addToConversationHistory: (messages: ChatMessage[]) => void;
  clearConversationHistory: () => void;
  setHidden: (hidden: boolean) => void;
  // Request new session (incremented to trigger new session in sidebar)
  newSessionRequest: number;
  requestNewSession: () => void;
  /** When set, mounted Ani Chat consumes and sends this as a user message */
  outboundUserMessage: OutboundUserMessagePayload | null;
  /**
   * Opens Ani (sidebar or widget) and queues a user message for the chat to send.
   *
   * @param content - Message text (whitespace-only values are ignored)
   */
  sendUserMessageToAni: (content: string) => void;
  clearOutboundUserMessage: () => void;
  /** Announcements waiting to be shown by the mounted Ani panel, oldest first. */
  aniAnnouncements: AniAnnouncement[];
  announceToAni: (announcement: AniAnnouncement) => void;
  consumeAniAnnouncement: (id: string) => void;
  aniFileDropHandler: AniFileDropHandler | null;
  setAniFileDropHandler: (handler: AniFileDropHandler | null) => void;
}

// Create the React Context with an undefined default value
const AniContext = createContext<AniContextProps | undefined>(undefined);

/**
 * AniProvider component that makes AniChatWidget configuration available to its children.
 * It allows setting an initial configuration.
 */
export const AniProvider: React.FC<{
  children: React.ReactNode;
  initialConfig?: AniConfig;
}> = ({ children, initialConfig }) => {
  const [config, setConfig] = useState<AniConfig>(initialConfig || {});
  const [widgetViewState, setWidgetViewStateState] = useState<AniWidgetViewState>(() => ({
    ...DEFAULT_ANI_WIDGET_VIEW_STATE,
    personalityMode: getStoredPersonalityMode(),
    isOpen: getStoredSidebarOpen(),
    isSpaceReady: getStoredSidebarOpen(),
    dockedWidth: getStoredSidebarWidth(),
  }));

  // Store conversations by context key
  const [conversationsByContext, setConversationsByContext] = useState<
    Record<string, ChatMessage[]>
  >({});

  // Counter to request new session in sidebar
  const [newSessionRequest, setNewSessionRequest] = useState(0);

  const [outboundUserMessage, setOutboundUserMessage] =
    useState<OutboundUserMessagePayload | null>(null);
  const openQueueTimeoutRef = useRef<number | null>(null);

  const clearOutboundUserMessage = useCallback(() => {
    setOutboundUserMessage(null);
  }, []);

  const [aniAnnouncements, setAniAnnouncements] = useState<AniAnnouncement[]>([]);
  const announceToAni = useCallback((announcement: AniAnnouncement) => {
    setAniAnnouncements((prev) => [...prev, announcement]);
  }, []);
  const consumeAniAnnouncement = useCallback((id: string) => {
    setAniAnnouncements((prev) => prev.filter((announcement) => announcement.id !== id));
  }, []);
  const [aniFileDropHandler, setAniFileDropHandler] = useState<AniFileDropHandler | null>(null);

  useEffect(() => {
    return () => {
      if (openQueueTimeoutRef.current !== null) {
        window.clearTimeout(openQueueTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (initialConfig) {
      setConfig(initialConfig);
    }
  }, [initialConfig]);

  // Get current conversation history based on active context
  const conversationHistory = useMemo(() => {
    const contextKey = config.chatContext || "default";
    return conversationsByContext[contextKey] || [];
  }, [config.chatContext, conversationsByContext]);

  // Set conversation history for current context
  const setConversationHistory = useCallback(
    (history: ChatMessage[]) => {
      const contextKey = config.chatContext || "default";
      setConversationsByContext((prev) => ({
        ...prev,
        [contextKey]: history,
      }));
    },
    [config.chatContext]
  );

  // Add messages to conversation history for current context
  const addToConversationHistory = useCallback(
    (messages: ChatMessage[]) => {
      const contextKey = config.chatContext || "default";
      setConversationsByContext((prev) => ({
        ...prev,
        [contextKey]: [...(prev[contextKey] || []), ...messages],
      }));
    },
    [config.chatContext]
  );

  // Clear conversation history for current context
  const clearConversationHistory = useCallback(() => {
    const contextKey = config.chatContext || "default";
    setConversationsByContext((prev) => ({
      ...prev,
      [contextKey]: [],
    }));
  }, [config.chatContext]);

  // Request a new session in the sidebar
  const requestNewSession = useCallback(() => {
    setNewSessionRequest((prev) => prev + 1);
  }, []);

  // Callback to update the entire config or parts of it
  const setAniConfig = useCallback((newConfig: Partial<AniConfig>) => {
    setConfig((prevConfig) => ({ ...prevConfig, ...newConfig }));
  }, []);

  // Individual setter for resourceId
  const setResourceId = useCallback((id: string | undefined) => {
    setConfig((prevConfig) => ({ ...prevConfig, resourceId: id }));
  }, []);

  // Individual setter for chatContext
  const setChatContext = useCallback((context: AniChatWidgetContextValue) => {
    setConfig((prevConfig) => ({ ...prevConfig, chatContext: context }));
  }, []);

  // Individual setter for apiData
  const setApiData = useCallback((data: unknown) => {
    setConfig((prevConfig) => ({ ...prevConfig, apiData: data }));
  }, []);

  // Individual setter for onApiAction callback
  const setOnApiAction = useCallback((callback: AniConfig["onApiAction"]) => {
    setConfig((prevConfig) => ({ ...prevConfig, onApiAction: callback }));
  }, []);

  // Individual setter for samplePrompts
  const setSamplePrompts = useCallback((prompts: string[] | undefined) => {
    setConfig((prevConfig) => ({ ...prevConfig, samplePrompts: prompts }));
  }, []);

  const setAppSamplePrompts = useCallback((prompts: Record<string, string[]> | undefined) => {
    setConfig((prevConfig) => ({ ...prevConfig, appSamplePrompts: prompts }));
  }, []);

  // Individual setter for initialAssistantMessage
  const setInitialAssistantMessage = useCallback(
    (message: string | undefined) => {
      setConfig((prevConfig) => ({
        ...prevConfig,
        initialAssistantMessage: message,
      }));
    },
    []
  );

  const setWidgetViewState = useCallback(
    (newState: Partial<AniWidgetViewState>) => {
      setWidgetViewStateState((prevState) => {
        const nextState = {
          ...prevState,
          ...newState,
          floatingPosition: {
            ...prevState.floatingPosition,
            ...(newState.floatingPosition || {}),
          },
          floatingSize: {
            ...prevState.floatingSize,
            ...(newState.floatingSize || {}),
          },
        };

        // Persist sidebar open state to localStorage
        if (newState.isOpen !== undefined) {
          localStorage.setItem(SIDEBAR_OPEN_STORAGE_KEY, String(nextState.isOpen));
        }
        if (newState.dockedWidth !== undefined) {
          localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(nextState.dockedWidth));
        }

        return nextState;
      });
    },
    []
  );

  const toggleWidgetOpen = useCallback(() => {
    setWidgetViewStateState((prevState) => {
      const nextOpen = !prevState.isOpen;
      localStorage.setItem(SIDEBAR_OPEN_STORAGE_KEY, String(nextOpen));
      return {
        ...prevState,
        isOpen: nextOpen,
      };
    });
  }, []);

  /**
   * Opens the Ani panel and queues a message so Chat sends it like a user submission.
   */
  const sendUserMessageToAni = useCallback(
    (content: string) => {
      const trimmed = content.trim();
      if (!trimmed) return;

      if (openQueueTimeoutRef.current !== null) {
        window.clearTimeout(openQueueTimeoutRef.current);
      }

      // When Ani is closed, give the chat panel time to mount before queuing.
      // This avoids send races during open animations.
      if (!widgetViewState.isOpen) {
        openQueueTimeoutRef.current = window.setTimeout(() => {
          setOutboundUserMessage({ id: Date.now(), content: trimmed });
          openQueueTimeoutRef.current = null;
        }, 320);
      } else {
        setOutboundUserMessage({ id: Date.now(), content: trimmed });
      }

      setWidgetViewState({ isOpen: true });
    },
    [setWidgetViewState, widgetViewState.isOpen]
  );

  const setDisplayMode = useCallback((mode: AniDisplayMode) => {
    setWidgetViewStateState((prevState) => ({
      ...prevState,
      displayMode: mode,
    }));
  }, []);

  const toggleDisplayMode = useCallback(() => {
    setWidgetViewStateState((prevState) => ({
      ...prevState,
      displayMode: prevState.displayMode === "normal" ? "developer" : "normal",
    }));
  }, []);

  const setPersonalityMode = useCallback((mode: AniPersonalityMode) => {
    setWidgetViewStateState((prevState) => ({
      ...prevState,
      personalityMode: mode,
    }));
    localStorage.setItem(PERSONALITY_MODE_STORAGE_KEY, mode);
  }, []);

  // Individual setter for hidden
  const setHidden = useCallback((hidden: boolean) => {
    setConfig((prevConfig) => ({ ...prevConfig, hidden }));
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      ...config,
      setAniConfig,
      setResourceId,
      setChatContext,
      setApiData,
      setOnApiAction,
      setSamplePrompts,
      setAppSamplePrompts,
      setInitialAssistantMessage,
      widgetViewState,
      setWidgetViewState,
      toggleWidgetOpen,
      setDisplayMode,
      toggleDisplayMode,
      setPersonalityMode,
      conversationHistory,
      setConversationHistory,
      addToConversationHistory,
      clearConversationHistory,
      setHidden,
      newSessionRequest,
      requestNewSession,
      outboundUserMessage,
      sendUserMessageToAni,
      clearOutboundUserMessage,
      aniAnnouncements,
      announceToAni,
      consumeAniAnnouncement,
      aniFileDropHandler,
      setAniFileDropHandler,
    }),
    [
      config,
      setAniConfig,
      setResourceId,
      setChatContext,
      setApiData,
      setOnApiAction,
      setSamplePrompts,
      setAppSamplePrompts,
      setInitialAssistantMessage,
      widgetViewState,
      setWidgetViewState,
      toggleWidgetOpen,
      setDisplayMode,
      toggleDisplayMode,
      setPersonalityMode,
      conversationHistory,
      setConversationHistory,
      addToConversationHistory,
      clearConversationHistory,
      setHidden,
      newSessionRequest,
      requestNewSession,
      outboundUserMessage,
      sendUserMessageToAni,
      clearOutboundUserMessage,
      aniAnnouncements,
      announceToAni,
      consumeAniAnnouncement,
      aniFileDropHandler,
    ]
  );

  return (
    <AniContext.Provider value={contextValue}>{children}</AniContext.Provider>
  );
};

/**
 * Custom hook to access the AniContext.
 * Throws an error if used outside of an AniProvider.
 */
export const useAni = (): AniContextProps => {
  const context = useContext(AniContext);
  if (context === undefined) {
    throw new Error("useAni must be used within an AniProvider");
  }
  return context;
};
